import React, { useMemo } from "react";
import styled from "styled-components";
import { WarningMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import { ClientScreenInputGrid, ThreeColumnsRow, TwoColumnsRow } from "../screens/ClientsScreen";
import { useSelector } from "react-redux";
import { PathwayFormGrid, PathwayFormGroup } from "../../../_UI/StyledComponents/PathwayForm";
import { Modes } from "../../../_statics/CRUDStatics";
import PathwayTextInputGroup from "../../../_UI/Components/Inputs/PathwayTextInputGroup";
import stringUtils from "../../../_services/string-utils";
import PathwaySwitchInputGroup from "../../../_UI/Components/Inputs/PathwaySwitchInputGroup";
import inputPattern from "../../../_statics/InputPattern";
import PathwaySelectInputGroup from "../../../_UI/Components/Inputs/PathwaySelectInputGroup";
import {
    AccountNumberLengthOptions,
    DatevDocumentTypeOptions,
    MonthOptions,
    SKROptions,
} from "../../../_components/SelectInputOptions";
import { PathwayButton, PathwayLinkButton } from "../../../_UI/Components/PathwayButtons";
import { InfoMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import { PathwayInput, PathwayLabel } from "../../../_UI/StyledComponents/PathwayForm";

const ClientInput = (props) => {
    const clientReducer = useSelector((state) => state.client);

    const isDatevConnected = useMemo(() => {
        return clientReducer.editClient && clientReducer.editClient.connectedToDatev;
    }, [clientReducer.editClient]);

    return (
        <ClientScreenInputGrid>
            <PathwayFormGrid>
                <PathwayTextInputGroup
                    label={"Name"}
                    name={"name"}
                    dataCy={"name-field-input"}
                    handleChange={props.handleEditClientChange}
                    value={clientReducer.editClient.name}
                    mandatory
                    autoFocus
                />
                <PathwayTextInputGroup
                    label={"Notizen"}
                    name={"note"}
                    dataCy={"note-field-input"}
                    handleChange={props.handleEditClientChange}
                    value={clientReducer.editClient.note}
                />
            </PathwayFormGrid>
            <PathwayFormGrid dataCy="datev-settings-wrapper">
                <DatevSectionLabel>DATEV – Einstellungen</DatevSectionLabel>

                {isDatevConnected && (
                    <InfoMessage marginless>
                        Du bist aktuell mit mindestens einem DATEV Buchungsdatenservice verbunden und kannst deshalb
                        keine DATEV-Änderungen vornehmen. Bitte entferne{" "}
                        <PathwayLinkButton onClick={props.redirectToDatevSettings}>hier</PathwayLinkButton> zunächst
                        alle Verbindungen und öffne anschließend die Account - Einstellungen erneut, um Änderungen
                        vorzunehmen.
                    </InfoMessage>
                )}
                {!clientReducer.isGlobalDatevSetting && clientReducer.editClientMode === Modes.CREATE && (
                    <PathwaySelectInputGroup
                        label="Existierende Datev Einstellungen"
                        name={"existingDatev"}
                        value={props.currentSelectedSettingIndex || ""}
                        handleChange={props.chooseExistingDatevSetting}
                    >
                        {clientReducer.usedDatevSettings.map((datevSetting, index) => (
                            <option key={index} value={index}>
                                Mandant: {datevSetting.consultantNumber}, Client: {datevSetting.clientNumber}, SKR:{" "}
                                {datevSetting.skr}
                            </option>
                        ))}
                    </PathwaySelectInputGroup>
                )}
                <TwoColumnsRow>
                    <PathwayTextInputGroup
                        label="Beraternummer"
                        name="consultantNumber"
                        dataCy="consultant-number-input"
                        pattern={inputPattern.number}
                        triggerOnBlur={stringUtils.clearWhiteSpace}
                        handleChange={props.handleEditClientChange}
                        value={clientReducer.editClient.consultantNumber}
                        disabled={isDatevConnected}
                    />
                    <PathwayTextInputGroup
                        label="Mandantennummer"
                        name="clientNumber"
                        dataCy="client-number-input"
                        pattern={inputPattern.number}
                        triggerOnBlur={stringUtils.clearWhiteSpace}
                        handleChange={props.handleEditClientChange}
                        value={clientReducer.editClient.clientNumber}
                        disabled={isDatevConnected}
                    />
                </TwoColumnsRow>
                <TwoColumnsRow>
                    <PathwaySelectInputGroup
                        label="Beginn des Wirtschaftsjahres (Monat)"
                        name={"businessYearMonth"}
                        dataCy={"business-year-month-select"}
                        handleChange={props.handleEditClientChange}
                        value={
                            clientReducer.editClient.businessYearMonth
                                ? clientReducer.editClient.businessYearMonth.toString()
                                : "1"
                        }
                        disabled={isDatevConnected}
                    >
                        <MonthOptions />
                    </PathwaySelectInputGroup>
                    <PathwayFormGroup>
                        <PathwayLabel>Beginn des Wirtschaftsjahres (Tag)</PathwayLabel>
                        <PathwayInput
                            type="number"
                            data-cy="business-year-day-number"
                            name="businessYearDay"
                            onChange={props.handleEditClientChange}
                            value={
                                clientReducer.editClient.businessYearDay
                                    ? clientReducer.editClient.businessYearDay.toString()
                                    : "1"
                            }
                            disabled={isDatevConnected}
                            min="1"
                            max={
                                clientReducer.editClient.businessYearMonth
                                    ? new Date(2001, clientReducer.editClient.businessYearMonth, 0).getDate()
                                    : 31
                            }
                        />
                    </PathwayFormGroup>
                </TwoColumnsRow>
                <ThreeColumnsRow>
                    <PathwaySelectInputGroup
                        label={"Sachkontennummernlänge (zwischen 4-8)"}
                        name={"accountNumberLength"}
                        dataCy={"account-number-length-select"}
                        handleChange={props.handleSkrSettingsChange}
                        value={clientReducer.editClient.accountNumberLength}
                        disabled={isDatevConnected}
                        nullOptionValue="Nicht bekannt"
                    >
                        <AccountNumberLengthOptions />
                    </PathwaySelectInputGroup>
                    <PathwaySelectInputGroup
                        label={"SKR"}
                        name={"skr"}
                        handleChange={props.handleSkrSettingsChange}
                        value={clientReducer.editClient.skr}
                        disabled={isDatevConnected}
                        dataCy={"skr-select"}
                        nullOptionValue="Nicht bekannt"
                    >
                        <SKROptions />
                    </PathwaySelectInputGroup>
                    <PathwayTextInputGroup
                        label="Buchungsstapel Suffix"
                        name="taskSuffix"
                        dataCy="task-suffix-input"
                        pattern={inputPattern.alphaNumeric}
                        triggerOnBlur={stringUtils.clearWhiteSpace}
                        handleChange={props.handleEditClientChange}
                        value={clientReducer.editClient.taskSuffix}
                        tooltip={
                            "Dieses Suffix wird an den Titel des Buchungsstapels angehängt und später in DATEV angezeigt. " +
                            "Damit ist es leichter die Buchungsstapel von mehreren Accounts oder Shops auseinanderzuhalten. " +
                            "Bitte maximal 5 Zeichen eingeben, die Länge ist bei DATEV eingeschränkt. "
                        }
                    />
                </ThreeColumnsRow>
                <TwoColumnsRow>
                    <PathwaySwitchInputGroup
                        onChange={props.handleEditClientChange}
                        value={clientReducer.editClient.includeDeliveryDate}
                        name="includeDeliveryDate"
                        dataCy={"include-delivery-date"}
                        label="Leistungsdatum exportieren"
                    />
                </TwoColumnsRow>
                <TwoColumnsRow>
                    <PathwaySelectInputGroup
                        label={"Belegtyp der übertragenen Dokumente"}
                        name={"datevDocumentType"}
                        handleChange={props.handleDatevDocumentTypeChange}
                        value={clientReducer.editClient.datevDocumentType ?? ""}
                        nullOptionValue={"Nicht übertragen"}
                        dataCy={"datev-document-type-select"}
                        tooltip={
                            "Mit diesem Belegtyp werden die Dokumente an DATEV übertragen - über die " +
                            "DATEV-Schnittstelle ebenso wie im XML-Export für den DATEV Belegtransfer. In der DATEV " +
                            "Belegfreigabe lassen sich Belege nur anhand ihres Belegtyps vom Freigabeprozess " +
                            "ausnehmen - ohne Belegtyp muss jeder Beleg einzeln bestätigt werden. Bei der " +
                            "Übertragung über die DATEV-Schnittstelle muss der Belegtyp in DATEV Belege online " +
                            "angelegt sein, sonst werden die Dokumente ohne Belegtyp übertragen."
                        }
                    >
                        <DatevDocumentTypeOptions />
                    </PathwaySelectInputGroup>
                </TwoColumnsRow>
            </PathwayFormGrid>
            <WarningMessage
                isOpen={props.skrSettingsUpdated && !props.skrUpdateConfirmed}
                data-cy={"global-skr-settings-change-alert"}
            >
                <h4 className="alert-heading">Achtung!</h4>
                <p>
                    Wir werden deine derzeitige Konfiguration auf den neu gewählten Kontenrahmen-Plan (SKR) umändern.
                    Das bedeutet, deine Umsatzkonten werden nun durch die Standardkonten des gewählten SKR ersetzt. Wenn
                    du deine alten Imports ändern möchtest, musst du diese manuell erstellen.
                    <br />
                    <br />
                    <a
                        href="https://hilfe.pathway-solutions.de/de/knowledge/shopify-datev-export-kontenvorschl%C3%A4ge"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Hier
                    </a>{" "}
                    findest du unsere Shopify Kontenvorschläge.
                    <br />
                    <a
                        href="https://hilfe.pathway-solutions.de/de/knowledge/saas-kontenvorschl%C3%A4ge-f%C3%BCr-stripe-billing"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Hier
                    </a>{" "}
                    sind unsere SaaS Kontenvorschläge.
                    <br />
                    <br />
                    <strong>Diese Änderung könnte einige Minuten in Anspruch nehmen.</strong>
                </p>
                <hr />
                <TwoColumnsRow>
                    <PathwayButton
                        color="danger"
                        onClick={props.confirmSkrUpdateForExistingIntegrations}
                        dataCy={"apply-global-skr-settings-change-button"}
                    >
                        Ja, Setup zurücksetzen und Standard-Konten anwenden
                    </PathwayButton>
                    <PathwayButton
                        color="secondary"
                        onClick={props.confirmSkrUpdate}
                        dataCy={"apply-regular-skr-settings-change-button"}
                    >
                        Nein, alle Einstellungen behalten und nur SKR wechseln
                    </PathwayButton>
                </TwoColumnsRow>
            </WarningMessage>
        </ClientScreenInputGrid>
    );
};

export default ClientInput;

const DatevSectionLabel = styled.div`
    font-size: var(--pw-text-md);
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
    padding-bottom: 8px;
    border-bottom: 1px solid var(--border-hairline);
`;
